import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    page_slug: '',
    order: 0
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchFAQs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faqs`, {
        withCredentials: true
      });
      setFaqs(response.data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/api/faqs`,
        formData,
        { withCredentials: true }
      );
      setFaqs([...faqs, response.data]);
      setFormData({ question: '', answer: '', page_slug: '', order: 0 });
      setShowAddForm(false);
      setMessage({ type: 'success', text: 'FAQ toegevoegd!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Toevoegen mislukt' });
    }
  };

  const handleUpdate = async (faqId) => {
    try {
      const faq = faqs.find(f => f.id === faqId);
      await axios.put(
        `${API_URL}/api/faqs/${faqId}`,
        {
          question: faq.question,
          answer: faq.answer,
          order: faq.order
        },
        { withCredentials: true }
      );
      setEditingId(null);
      setMessage({ type: 'success', text: 'FAQ bijgewerkt!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Bijwerken mislukt' });
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Weet je zeker dat je deze FAQ wilt verwijderen?')) return;

    try {
      await axios.delete(`${API_URL}/api/faqs/${faqId}`, {
        withCredentials: true
      });
      setFaqs(faqs.filter(f => f.id !== faqId));
      setMessage({ type: 'success', text: 'FAQ verwijderd' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Verwijderen mislukt' });
    }
  };

  const updateFAQ = (id, field, value) => {
    setFaqs(faqs.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">FAQ&apos;s Beheren</h1>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-700 hover:bg-blue-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe FAQ
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        {showAddForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Nieuwe FAQ Toevoegen</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vraag *
                  </label>
                  <Input
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    placeholder="Wat is Zoho CRM?"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Antwoord *
                  </label>
                  <Textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    placeholder="Zoho CRM is een..."
                    rows={4}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pagina Slug *
                    </label>
                    <Input
                      value={formData.page_slug}
                      onChange={(e) => setFormData({ ...formData, page_slug: e.target.value })}
                      placeholder="/zoho-crm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Volgorde
                    </label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-700 hover:bg-blue-800">
                    Toevoegen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Annuleren
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Alle FAQ&apos;s ({faqs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {faqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">Nog geen FAQ&apos;s toegevoegd</p>
                <Button onClick={() => setShowAddForm(true)}>
                  Voeg je eerste FAQ toe
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <Card key={faq.id} className="border-2">
                    <CardContent className="p-4">
                      {editingId === faq.id ? (
                        <div className="space-y-4">
                          <Input
                            value={faq.question}
                            onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                            placeholder="Vraag"
                          />
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                            placeholder="Antwoord"
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleUpdate(faq.id)}
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Opslaan
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Annuleren
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 mb-1">
                                {faq.question}
                              </h3>
                              <p className="text-sm text-gray-600">{faq.answer}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                Pagina: {faq.page_slug} | Volgorde: {faq.order}
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingId(faq.id)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(faq.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FAQManager;